/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Card, CardActions, CardContent, Chip, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { templatesAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import TemplateDialog from '../../components/templates/TemplateDialog'
import { contentsSummary, templateIcon } from '../../components/templates/templateModel'
import { useNotify } from '../../components/common/NotificationProvider'
import { entityLabelPlural, formatDate } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'

/** Gallery of ready-made setups per kind of business; installing creates ordinary, editable configuration. */
const TemplatesPage = () => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const notify = useNotify()
  const [previewKey, setPreviewKey] = useState(null)
  const [removing, setRemoving] = useState(null)
  const { data: templates, isLoading, error } = useQuery({ queryKey: [tenantId, 'templates'], queryFn: templatesAPI.list })

  const install = useMutation({
    mutationFn: ({ template, sample }) => templatesAPI.install(template.key, sample),
    onSuccess: (result, { template }) => {
      // forms, record types, roles, views, dashboards, automations: everything for this tenant changed
      queryClient.invalidateQueries({ queryKey: [tenantId] })
      setPreviewKey(null)
      const skipped = result.skipped.length ? ` Skipped: ${result.skipped.join('; ')}` : ''
      notify(`${template.name} installed.${skipped}`)
      navigate(`/records/${result.firstEntityType}`)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })
  const removeSample = useMutation({
    mutationFn: (installId) => templatesAPI.removeSampleData(installId),
    onSuccess: (r) => {
      setRemoving(null)
      queryClient.invalidateQueries({ queryKey: [tenantId] })
      notify(`Removed ${r.deleted} example records`)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>
  const preview = templates.find((t) => t.key === previewKey)

  return (
    <>
      <PageHeader title="Templates" subtitle="Start from a ready-made setup for your kind of business, then adjust it to fit. Templates add record types, automations, roles, views and a dashboard." />
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' } }}>
        {templates.map((t) => {
          const Icon = templateIcon(t.icon)
          const latest = t.installs[0]
          const canRemoveSample = latest && latest.sampleRecords > 0 && !latest.sampleRemoved
          return (
            <Card key={t.key} variant="outlined" sx={{ display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'action.hover', display: 'flex' }}><Icon color="primary" /></Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{t.industry}</Typography>
                  </Box>
                  {latest && <Chip icon={<CheckCircleOutlineIcon />} label="Installed" color="success" variant="outlined" />}
                </Stack>
                <Typography variant="body2" sx={{ mt: 1.5 }}>{t.description}</Typography>
                <Box component="ul" sx={{ pl: 2.5, my: 1.5, '& li': { typography: 'body2', color: 'text.secondary', mb: 0.25 } }}>
                  {t.highlights.map((h) => <li key={h}>{h}</li>)}
                </Box>
                <Typography variant="caption" color="text.secondary" component="div">
                  {t.recordTypes.map((r) => entityLabelPlural(r.entityType)).join(', ')} · {contentsSummary(t)}
                </Typography>
                {latest && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Installed {formatDate(latest.installedOn)} by {latest.installedBy}
                    {latest.sampleRecords > 0 && (latest.sampleRemoved ? ' · example records removed' : ` · ${latest.sampleRecords} example records`)}
                  </Typography>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2, gap: 1, flexWrap: 'wrap' }}>
                <Button variant={latest ? 'outlined' : 'contained'} onClick={() => setPreviewKey(t.key)}>
                  {latest || t.conflicts.length ? 'View details' : 'Preview & install'}
                </Button>
                {canRemoveSample && <Button color="inherit" onClick={() => setRemoving({ template: t, install: latest })}>Remove example records</Button>}
              </CardActions>
            </Card>
          )
        })}
      </Box>

      <TemplateDialog key={previewKey} template={preview} open={!!preview} busy={install.isPending}
        onInstall={(template, sample) => install.mutate({ template, sample })} onClose={() => setPreviewKey(null)} />
      <ConfirmDialog
        open={!!removing}
        title="Remove example records?"
        message={removing ? `The ${removing.install.sampleRecords} example records added with ${removing.template.name} will be deleted (records you created are kept).` : ''}
        confirmLabel="Remove"
        destructive
        busy={removeSample.isPending}
        onConfirm={() => removeSample.mutate(removing.install.id)}
        onClose={() => setRemoving(null)}
      />
    </>
  )
}

export default TemplatesPage
